import type { EnhancedStateLawProfile } from "@/data/enhancedStateLawProfiles";

export const NORTHEAST_STATE_LAW_PROFILES: EnhancedStateLawProfile[] = [
  {
    stateCode: "CT",
    distinctiveRule:
      "Connecticut requires fifteen years of uninterrupted adverse use and gives an owner a specific statutory notice procedure to interrupt a claim before it ripens.",
    overview:
      "Connecticut prescriptive easements rest on a single statute rather than the adverse-possession framework. A claimant must show open, visible, continuous use of a defined path or right-of-way for fifteen years, made under a claim of right rather than as a favor. Connecticut law does not presume permissive use, but it also gives owners a formal way to stop the clock before the period runs by serving and recording a statutory notice disputing the use.",
    burden:
      "The party claiming the easement bears the burden of proving each element by a fair preponderance of the evidence. Because Connecticut applies no presumption that long use was permissive, the claimant's task is often about showing a definite route and open conduct rather than overcoming a hostile presumption from the owner.",
    elements: [
      {
        name: "Open and visible use",
        explanation:
          "The use must be apparent enough that an attentive owner would notice it, such as a worn path, a maintained driveway, or a visible utility line.",
      },
      {
        name: "Continuous use for fifteen years",
        explanation:
          "The use must persist without an effective break for the entire statutory period in the manner ordinary for that kind of easement.",
      },
      {
        name: "Use under a claim of right",
        explanation:
          "The claimant's conduct must communicate an assertion of entitlement rather than dependence on the owner's goodwill or a revocable arrangement.",
      },
      {
        name: "No effective interruption or statutory notice",
        explanation:
          "An owner can stop the running of the period by serving and recording the statutory notice disputing the claimed easement; a claimant must show the full fifteen years ran without such an interruption.",
      },
    ],
    pitfalls: [
      "A recorded notice disputing the use under the interruption statute restarts the clock, so a claimant must trace the record before assuming years of use all count toward the period.",
      "Use that began with a landlord's or neighbor's express permission remains permissive until the claimant's conduct clearly changes to an assertion of right.",
      "Vague testimony that people 'always' used a driveway is weaker than dated photographs, maintenance records, or witness accounts tied to specific years.",
      "A prescriptive-easement claim over a defined path should not be confused with a broader adverse-possession claim to the underlying land, which has its own separate proof requirements.",
    ],
    workedExample: {
      title: "A bakery's shared loading lane in New Haven",
      facts:
        "A shop owner has used a neighbor's paved lane to reach a loading dock for eighteen years, never asking permission and never being told to stop, while suppliers and haulers used the same lane regularly.",
      analysis:
        "Eighteen years clears the fifteen-year statutory period, and consistent commercial use without permission supports an inference of a claim of right. The owner's silence during that time, rather than active consent, cuts against a finding of permissive use.",
      nextStep:
        "Gather delivery logs, photographs, resurfacing invoices, and any correspondence with the neighbor to document when the use began and that no notice under the interruption statute was ever served.",
    },
    recordingGuidance:
      "Connecticut land records are kept by the town clerk in the town where the property lies, not by a county office. A negotiated easement agreement should be recorded on the land records of that town, with a legal description, width, and permitted uses; check the specific town clerk's current recording requirements before filing.",
    caseExample: {
      name: "DiSorbo v. Grand Associates One Ltd. Partnership",
      citation: "8 Conn. App. 203, 512 A.2d 940 (1986)",
      facts:
        "A bakery owner and her husband used a driveway and crossover on a neighboring commercial lot in New Haven for deliveries and trash pickup for more than twenty years, without ever asking permission or being told to stop.",
      outcome:
        "The Connecticut Appellate Court upheld the trial court's finding of a prescriptive easement, concluding that the unexplained, unpermitted, long-term use supported the finding that the use was adverse.",
      lesson:
        "Sustained commercial use of a defined route, carried on without seeking or receiving permission, can support a prescriptive claim even without a dramatic confrontation with the owner.",
      sourceUrl: "https://law.justia.com/cases/connecticut/court-of-appeals/1986/8-conn-app-203.html",
    },
    sources: [
      {
        label: "Connecticut prescriptive-easement statute",
        citation: "Conn. Gen. Stat. § 47-37",
        url: "https://www.cga.ct.gov/current/pub/chap_822.htm",
      },
      {
        label: "Connecticut procedure for interrupting a claimed easement",
        citation: "Conn. Gen. Stat. §§ 47-38 to 47-40",
        url: "https://www.cga.ct.gov/current/pub/chap_822.htm",
      },
    ],
  },
  {
    stateCode: "DE",
    distinctiveRule:
      "Delaware's twenty-year prescriptive period comes from the real-actions limitations statute rather than a dedicated easement statute, and Delaware courts demand clear and convincing proof of adversity.",
    overview:
      "Delaware has no separate prescriptive-easement statute; courts borrow the twenty-year period from the state's real-actions limitations law, the same statute that underlies adverse possession of land. A claimant must show open, notorious, continuous, and exclusive use under a claim of right for the full twenty years, and Delaware decisions have held claimants to a demanding, clear-and-convincing standard rather than a bare preponderance.",
    burden:
      "The claimant must prove adverse use by clear and convincing evidence. Delaware courts have rejected prescriptive claims where the record showed only ordinary neighborly travel rather than conduct that put the owner on notice of a hostile claim.",
    elements: [
      {
        name: "Open and notorious use",
        explanation:
          "The use of the road, path, or utility line must be visible enough that a reasonably attentive owner would learn of it.",
      },
      {
        name: "Continuous, uninterrupted use for twenty years",
        explanation:
          "The claimed use must persist without a material gap for the full statutory period drawn from the real-actions statute.",
      },
      {
        name: "Adverse use under a claim of right",
        explanation:
          "The claimant must show conduct inconsistent with the owner's right to exclude, not a favor extended between neighbors or family.",
      },
      {
        name: "Clear and convincing proof",
        explanation:
          "Because Delaware holds claimants to this heightened standard, conflicting or ambiguous testimony about the origin of the use tends to defeat the claim.",
      },
    ],
    pitfalls: [
      "Delaware's heightened clear-and-convincing standard means ambiguous or thin testimony about how the use began is often fatal to the claim.",
      "A right of way granted informally by a prior owner, even decades ago, remains permissive unless the claimant shows a later, unmistakable assertion of an adverse right.",
      "Family relationships between neighboring owners can make courts especially skeptical that use was hostile rather than accommodated.",
      "A driveway dispute framed as a right-of-way should not be mixed with a claim to title in the strip of land itself, which follows a different adverse-possession analysis under the same twenty-year statute.",
    ],
    workedExample: {
      title: "A shared garage driveway near Lewes",
      facts:
        "A homeowner has driven across the rear of a neighboring lot to reach a detached garage for twenty-two years. The neighbor never objected, but the two families have always been on friendly terms and occasionally shared plowing costs.",
      analysis:
        "The twenty-two years clears the statutory period, but the friendly, cooperative relationship and shared costs are the kind of facts Delaware courts have found consistent with permissive accommodation rather than a hostile claim, especially under the clear-and-convincing standard.",
      nextStep:
        "Document the earliest use, any statements about a right to cross (rather than a favor), and whether either family ever paid for or acknowledged an easement, before assuming the friendly history helps the claim.",
    },
    recordingGuidance:
      "Delaware records real property instruments with the recorder of deeds in the county where the land lies. An easement agreement should include a metes-and-bounds or plotted description, width, and permitted uses, and should be checked against that county recorder's current filing requirements.",
    caseExample: {
      name: "Bertrand v. Hill",
      citation: "173 A.2d 615 (Del. 1961)",
      facts:
        "A landowner began driving across a neighboring lot to reach a garage, relying on more than twenty years of use by prior occupants of the property as the basis for a claimed prescriptive right of way.",
      outcome:
        "The Delaware Supreme Court affirmed judgment for the landowner being crossed, holding that the claimant had not presented clear and convincing proof of the adverse use required to establish a prescriptive right.",
      lesson:
        "Long use alone does not establish a prescriptive easement in Delaware; the claimant must meet a clear-and-convincing standard on every element, including adversity.",
      sourceUrl: "https://case-law.vlex.com/vid/bertrand-v-hill-894914092",
    },
    sources: [
      {
        label: "Delaware twenty-year real-actions limitations statute",
        citation: "10 Del. C. §§ 7901, 7902",
        url: "https://delcode.delaware.gov/title10/c079/index.html",
      },
      {
        label: "Delaware recorder of deeds authority",
        citation: "9 Del. C. § 3556",
        url: "https://delcode.delaware.gov/title9/c035/index.html",
      },
    ],
  },
  {
    stateCode: "ME",
    distinctiveRule:
      "Maine allows an owner to head off a prescriptive claim entirely by posting or recording a statutory notice, a preventive tool most other states do not offer in this form.",
    overview:
      "Maine's prescriptive-easement statute sets a twenty-year period of uninterrupted adverse use and enjoyment of a right-of-way or other easement. Maine courts have also developed a presumption that unexplained, open, and continuous use for the statutory period is adverse, shifting the practical focus to whether the owner can show the use was permissive, such as through a friendly-neighbor relationship. The same statute lets a concerned owner post notice on the premises or record notice in the registry of deeds to prevent the period from ever running.",
    burden:
      "The claimant must establish continuous use for twenty years, after which Maine's presumption of adversity can place the practical burden on the owner to come forward with evidence of permission or a friendly-neighbor arrangement.",
    elements: [
      {
        name: "Use continued for twenty years",
        explanation:
          "The claimant must show the same basic use of a defined route or right persisted without interruption for two full decades.",
      },
      {
        name: "Open, visible use giving notice",
        explanation:
          "The use must be apparent enough that the owner's knowledge and acquiescence can be presumed, even without direct proof the owner watched it happen.",
      },
      {
        name: "No effective statutory notice posted or recorded",
        explanation:
          "An owner who posts notice on the property or records notice in the registry of deeds during the period can prevent the claim from maturing, so a claimant must confirm no such notice interrupted the twenty years.",
      },
      {
        name: "Absence of a controlling permissive relationship",
        explanation:
          "Even after twenty years of open use, evidence that the parties had a cooperative, friendly-neighbor understanding can undercut the presumption that the use was adverse.",
      },
    ],
    pitfalls: [
      "A landowner's posted or recorded statutory notice can defeat an otherwise complete prescriptive claim, so the absence of such notice should be confirmed in the registry.",
      "Courts scrutinize whether the parties' relationship looked like a friendly accommodation rather than an assertion of right, particularly among relatives or long-time neighbors.",
      "A parking or access easement claimed against one small area cannot be stretched into a broader right the evidence does not support.",
      "A prescriptive easement claim is distinct from an easement by necessity, which instead depends on prior common ownership and a lack of other access when the parcels were divided.",
    ],
    workedExample: {
      title: "A shoreline footpath used for three decades",
      facts:
        "A family has walked the same path along the shore across a neighbor's waterfront lot for thirty years to reach the water, without asking permission, while the neighbor never objected or posted any notice.",
      analysis:
        "Thirty years comfortably exceeds Maine's twenty-year period, and the open, unexplained use supports a presumption of adversity. Without evidence of a recorded or posted notice, or of a cooperative arrangement between the families, the presumption is likely to stand.",
      nextStep:
        "Search the registry of deeds for any notice recorded by the neighbor during the relevant decades and gather photographs or witness statements establishing the path's route and duration.",
    },
    recordingGuidance:
      "Maine records real property instruments, including easements and the statutory notices that can interrupt a prescriptive claim, in the registry of deeds for the county where the land is located. Confirm the specific registry's indexing and fee schedule before recording an agreement or a notice.",
    caseExample: {
      name: "Riffle v. Smith",
      citation: "2014 ME 21",
      facts:
        "Neighbors and their predecessors had used a small triangular section of an adjoining lot for parking and access for several decades before the current owners built a fence blocking that use.",
      outcome:
        "The Maine Supreme Judicial Court affirmed a judgment awarding a prescriptive easement, declining to adopt a broad friendly-neighbor exception where the trial court had not found facts showing a friendly-neighbor relationship during the relevant period.",
      lesson:
        "Decades of open, unexplained use can support a prescriptive easement in Maine unless the record shows a genuine friendly-neighbor accommodation undercutting the presumption of adversity.",
      sourceUrl: "https://law.justia.com/cases/maine/supreme-court/2014/2014-me-21.html",
    },
    sources: [
      {
        label: "Maine prescriptive-easement statute and notice procedure",
        citation: "14 M.R.S. § 812",
        url: "https://legislature.maine.gov/statutes/14/title14sec812.html",
      },
      {
        label: "Maine registry of deeds structure",
        citation: "33 M.R.S. § 651",
        url: "https://legislature.maine.gov/statutes/33/title33sec651.html",
      },
    ],
  },
  {
    stateCode: "MD",
    distinctiveRule:
      "Maryland's twenty-year prescriptive period is common law, not statute, and Maryland presumes adversity from unexplained, uninterrupted use unless the relationship between the parties suggests permission.",
    overview:
      "Maryland preserves the common-law doctrine of prescription separately from its statutory twenty-year period for actions to recover land. A claimant must show use of a defined way that is open, continuous, and adverse for twenty years, and Maryland case law recognizes a presumption of adversity once open and continuous use for that period is shown, subject to rebuttal, for example by evidence of a family or otherwise permissive relationship between the properties.",
    burden:
      "The claimant must prove the underlying use and its duration; once open, continuous, unexplained use for twenty years is shown, the burden effectively shifts to the owner to produce evidence that the use was permissive.",
    elements: [
      {
        name: "Continuous use for twenty years",
        explanation:
          "The use of the claimed way must persist for the full period recognized under Maryland's common-law prescription doctrine.",
      },
      {
        name: "Open and unexplained use",
        explanation:
          "The use must be visible and not accompanied by an explanation, such as an express permission arrangement, that would prevent the presumption of adversity from arising.",
      },
      {
        name: "Exclusivity appropriate to the claimed right",
        explanation:
          "The claimant's use must be independent of merely sharing in general public or neighborhood travel over the same ground.",
      },
      {
        name: "No controlling permissive relationship",
        explanation:
          "Maryland courts examine family ties, landlord-tenant links, or other relationships that can make an otherwise qualifying use look like an accommodation instead of an adverse claim.",
      },
    ],
    pitfalls: [
      "A person who begins using a right-of-way while living on a relative's property as a minor, or otherwise dependent on the owner, may find the use presumed permissive rather than adverse.",
      "Use by invitees of someone who already has permission does not create a separate adverse claim in the invitee.",
      "Maryland's Real Property Article contains related but separate limitations periods for other rights, so a claim should be matched to the specific doctrine and statute in play.",
      "An outdated or informal survey can leave the exact boundary of the claimed way uncertain, undermining an otherwise strong prescriptive claim.",
    ],
    workedExample: {
      title: "A Baltimore County connector road serving several lots",
      facts:
        "Several unrelated landowners have used a gravel connector road across a neighbor's parcel for more than twenty years to reach a public road, without any family relationship among the parties and without ever asking permission.",
      analysis:
        "Because the users are unrelated neighbors rather than family members or tenants of the servient owner, the twenty years of open, unexplained use is more likely to trigger Maryland's presumption of adversity than in cases involving dependent or family relationships.",
      nextStep:
        "Confirm the exact width and route of the historical use through surveys and aerial photographs, and document that no landowner ever granted express permission for the road.",
    },
    recordingGuidance:
      "Maryland records deeds and easements with the clerk of the circuit court for the county, or with the equivalent land records office in Baltimore City, where the property is located. Confirm that office's current recording requirements and include a metes-and-bounds description matching any plat referenced in the agreement.",
    caseExample: {
      name: "Mahoney v. Devonshire, Inc.",
      citation: "86 Md. App. 624, 587 A.2d 1146 (1991)",
      facts:
        "Several corporations that owned adjoining developed lots near Reisterstown sued neighboring landowners to establish rights to a roadway connecting two county roads, relying in part on a jury finding of a prescriptive easement.",
      outcome:
        "The Court of Special Appeals of Maryland upheld the finding of a prescriptive easement and addressed the proper scope of the right the jury had found, including the extent to which the roadway could be improved and used.",
      lesson:
        "Even where a prescriptive easement is established, Maryland courts separately examine how far the scope of that easement can be expanded to match evolving uses.",
      sourceUrl: "https://law.justia.com/cases/maryland/court-of-special-appeals/1991/816-september-term-1990-0.html",
    },
    sources: [
      {
        label: "Maryland twenty-year limitations period and preservation of common-law prescription",
        citation: "Md. Code, Cts. & Jud. Proc. § 5-103",
        url: "https://law.justia.com/codes/maryland/courts-and-judicial-proceedings/title-5/subtitle-1/section-5-103/",
      },
      {
        label: "Maryland land-records recording framework",
        citation: "Md. Code, Real Prop. § 3-101",
        url: "https://law.justia.com/codes/maryland/real-property/title-3/subtitle-1/section-3-101/",
      },
    ],
  },
  {
    stateCode: "MA",
    distinctiveRule:
      "Massachusetts requires a prescriptive right of way to follow one regular, particularized route, and a claimant who cannot pin down that route loses the claim regardless of how long the use continued.",
    overview:
      "Massachusetts sets a twenty-year period for acquiring an easement by adverse use or enjoyment. Massachusetts case law adds a location requirement distinct from many states: an easement of passage by prescription must be substantially confined to a definite and consistent path, not a general area the claimant wandered across over the years. Courts also apply a presumption that unexplained use for twenty years is adverse, which an owner can rebut with evidence of permission.",
    burden:
      "The claimant must prove open, notorious, continuous use for twenty years and must also identify the particular route used throughout that period. Massachusetts courts have reversed judgments for claimants who proved long use but failed to establish a single consistent path.",
    elements: [
      {
        name: "A regular, particularized route",
        explanation:
          "The claimed right of way must follow a specific, identifiable path rather than shifting year to year across the servient land.",
      },
      {
        name: "Open and notorious use",
        explanation:
          "The use must be visible enough to give a reasonably attentive owner notice of the claim.",
      },
      {
        name: "Continuous use for twenty years",
        explanation:
          "The claimant must show the qualifying use persisted, in the manner ordinary for that kind of easement, throughout the full statutory period.",
      },
      {
        name: "Adverse use, not explained by permission",
        explanation:
          "Unexplained use for the period is presumed adverse, but evidence the owner gave permission, express or clearly implied, defeats the claim.",
      },
    ],
    pitfalls: [
      "Failing to prove one consistent route, rather than general wandering across a lot, has defeated Massachusetts prescriptive claims even after decades of use.",
      "A gap in occupancy or use, such as moving away from the property for several years, can interrupt the running of the twenty-year period.",
      "Permission from an owner, even informal, prevents the presumption of adversity from taking hold while that permission continues.",
      "A footpath prescriptive claim is separate from an easement by necessity, which instead depends on prior common ownership and lack of other access at severance.",
    ],
    workedExample: {
      title: "A childhood path revisited in adulthood",
      facts:
        "A homeowner walked and biked along a private way across a neighbor's land as a child, moved away for several years, and later resumed the same walk as an adult after returning to live on the family lot.",
      analysis:
        "Massachusetts courts look at whether the total qualifying use adds up to twenty years and whether the route stayed consistent. A multi-year absence from the property can complicate tacking the childhood and adult periods together, and the claimant must still show the path followed a single defined line.",
      nextStep:
        "Document the exact route used in both periods, the dates of any absence, and whether family members maintained a consistent, visible path throughout, before assuming all the years can be combined.",
    },
    recordingGuidance:
      "Massachusetts records deeds and easements with the registry of deeds for the county, or district, where the land lies, and land already registered under the Land Court system is recorded separately in that registry district. Confirm which system applies to the specific parcel before recording an agreement.",
    caseExample: {
      name: "Stone v. Perkins",
      citation: "59 Mass. App. Ct. 265, 795 N.E.2d 1207 (2003)",
      facts:
        "A landowner who had walked and bicycled along a private way across a neighboring lot, as a child and later as an adult, sought a prescriptive easement, but the trial court could not identify a single particular location for the crossing.",
      outcome:
        "The Massachusetts Appeals Court reversed the judgment recognizing the easement, holding that a prescriptive right of way must be substantially confined to a regular and particularized route, which the record here did not establish.",
      lesson:
        "Long, repeated use is not enough in Massachusetts; the claimant must also pin the right of way to one consistent, identifiable path.",
      sourceUrl: "https://law.justia.com/cases/massachusetts/court-of-appeals/volumes/59/59massappct265.html",
    },
    sources: [
      {
        label: "Massachusetts easements-by-prescription statute",
        citation: "Mass. Gen. Laws ch. 187, § 2",
        url: "https://malegislature.gov/Laws/GeneralLaws/PartII/TitleI/Chapter187/Section2",
      },
      {
        label: "Massachusetts registry of deeds structure",
        citation: "Mass. Gen. Laws ch. 36, § 1",
        url: "https://malegislature.gov/Laws/GeneralLaws/PartI/TitleVII/Chapter36/Section1",
      },
    ],
  },
  {
    stateCode: "NH",
    distinctiveRule:
      "New Hampshire has never codified prescriptive easement elements in statute; the doctrine rests entirely on case law layered onto the state's general twenty-year real-property limitations period.",
    overview:
      "New Hampshire borrows its twenty-year period from the general statute of limitations for actions to recover real estate, since the state has no dedicated prescriptive-easement statute. Courts require proof of adverse, continuous, and uninterrupted use for the full twenty years, with the owner's actual or imputed knowledge, and treat whether those elements are satisfied as a question of fact reviewed only for whether a reasonable factfinder could so find.",
    burden:
      "The claimant must prove adverse use for the entire period. New Hampshire courts give substantial deference to the trial court's factual findings on adversity and interruption, so the trial record and its details matter heavily on appeal.",
    elements: [
      {
        name: "Adverse use with the owner's knowledge",
        explanation:
          "The use must be inconsistent with the owner's exclusive rights and either known to the owner or open enough that knowledge can be inferred.",
      },
      {
        name: "Continuous and uninterrupted use for twenty years",
        explanation:
          "The use must persist for the full statutory period without an interruption caused by the owner asserting a paramount right or the claimant abandoning the use.",
      },
      {
        name: "A definite claimed route or use",
        explanation:
          "The evidence should identify a specific road, path, or utility line rather than a vague, shifting pattern of travel.",
      },
      {
        name: "No voluntary abandonment",
        explanation:
          "A temporary dispute over details, such as relocating a gate along the same road, does not necessarily interrupt use if the claimant's underlying right to use the road was never abandoned or successfully challenged.",
      },
    ],
    pitfalls: [
      "A minor dispute about incidental features of the route, such as a gate's exact placement, is not automatically the kind of interruption that resets the twenty-year clock.",
      "Because New Hampshire has no adverse-possession or prescriptive-easement statute setting out elements, claimants should expect the analysis to turn heavily on case law and the specific trial court's factual findings.",
      "Permission from a prior owner, if never revoked or converted into a hostile claim, keeps later use permissive rather than adverse.",
      "A prescriptive easement claim over a specific road is different from a claim to the land itself under general adverse-possession principles, which New Hampshire also treats as a common-law doctrine tied to the same twenty-year period.",
    ],
    workedExample: {
      title: "A lake-access road with a relocated gate",
      facts:
        "A family has used a road across a neighbor's land to reach their lake camp for twenty-five years. Partway through that period, the neighbor complained about a gate's location, and the family moved the gate further down the same road but never stopped using it.",
      analysis:
        "Because the underlying right to use the road was never successfully challenged and the family did not abandon the use, the gate dispute is unlikely by itself to interrupt the running of the twenty-year period, though the trial court's specific findings on the incident will matter.",
      nextStep:
        "Preserve any correspondence about the gate dispute, dated photographs of the road over time, and witness accounts confirming the use continued without a real challenge to the underlying right.",
    },
    recordingGuidance:
      "New Hampshire records deeds and easements with the register of deeds for the county where the property is located. Confirm that county register's current formatting and indexing rules, and include a clear description of the easement's route and width before recording.",
    caseExample: {
      name: "Cataldo v. Grappone",
      citation: "117 N.H. 1043, 380 A.2d 1087 (1977)",
      facts:
        "A family had used a road across a neighboring lot since purchasing their property in 1953, and the neighbor, who bought the adjoining lot decades later, built a barrier to block the road after a dispute over the location of a gate the family had built.",
      outcome:
        "The New Hampshire Supreme Court affirmed the finding of a prescriptive easement, holding that evidence of a dispute over the gate's exact location did not require a finding that the family's use of the road had been interrupted.",
      lesson:
        "A disagreement over incidental details of a right of way does not necessarily defeat a prescriptive claim if the underlying use of the road continued without real interruption.",
      sourceUrl: "https://law.justia.com/cases/new-hampshire/supreme-court/1977/7817-0.html",
    },
    sources: [
      {
        label: "New Hampshire twenty-year real-property limitations period",
        citation: "N.H. Rev. Stat. Ann. § 508:2",
        url: "https://gc.nh.gov/rsa/html/lii/508/508-2.htm",
      },
      {
        label: "New Hampshire register of deeds structure",
        citation: "N.H. Rev. Stat. Ann. § 478:1",
        url: "https://gc.nh.gov/rsa/html/XLVIII/478/478-1.htm",
      },
    ],
  },
  {
    stateCode: "NJ",
    distinctiveRule:
      "New Jersey applies a twenty-year period for prescriptive easements even though its adverse-possession statute for the underlying land requires thirty years, or sixty years for woodland or uncultivated tracts.",
    overview:
      "New Jersey courts apply a twenty-year period to establish a prescriptive easement, derived from the statute governing rights of entry into real estate, while a separate and much longer statute governs adverse possession of the fee itself. A claimant must show open, notorious, continuous, and exclusive use of a defined way under a claim of right for the full twenty years, and New Jersey courts have cautioned that proving hostility, as opposed to accommodation between neighbors, is often the central and most fact-intensive issue.",
    burden:
      "The claimant carries the burden of proving adverse use for twenty years. New Jersey decisions describe this as primarily a fact problem, and courts examine the full history of the relationship between the parties rather than treating any single period of use in isolation.",
    elements: [
      {
        name: "Open and notorious use",
        explanation:
          "The use must be visible enough to charge the owner with knowledge of it, whether through physical wear, structures, or regular visible travel.",
      },
      {
        name: "Continuous use for twenty years",
        explanation:
          "The use must continue in the manner ordinary for the claimed easement throughout the full statutory period.",
      },
      {
        name: "Exclusive use appropriate to the claim",
        explanation:
          "The claimant's use must be tied to their own benefited property rather than indistinguishable from general public or shared neighborhood use of the same area.",
      },
      {
        name: "Hostility distinct from neighborly accommodation",
        explanation:
          "New Jersey courts treat separating a true adverse claim from a cooperative, permissive arrangement between neighbors as a difficult factual question requiring close attention to how the use began and continued.",
      },
    ],
    pitfalls: [
      "New Jersey courts have called the adversity question primarily a fact problem, so thin or conclusory testimony about how a use began is a common reason claims fail.",
      "Occasional or intermittent use, such as an infrequent shortcut across a single track, may not satisfy the continuity element even if it recurs over many years.",
      "Confusing the twenty-year prescriptive-easement period with the much longer thirty- or sixty-year adverse-possession periods for the underlying fee can lead to miscalculating when a claim matured.",
      "A right used only in common with the general public, rather than tied to the claimant's own adjoining land, may fail to establish an individual prescriptive easement.",
    ],
    workedExample: {
      title: "A riding academy's bridle paths near Princeton",
      facts:
        "The owner of a riding academy has used bridle paths across two neighboring properties for over twenty years to reach open fields, with visible, regular horse traffic on one property but only occasional, unmarked use on the other.",
      analysis:
        "New Jersey's fact-intensive approach means the two properties may come out differently. The heavily used, visible path is more likely to satisfy the openness and continuity elements, while the occasional, unmarked crossing on the second property is a weaker candidate for a prescriptive right.",
      nextStep:
        "Document the frequency, visibility, and physical markers of use separately for each property, since New Jersey courts evaluate whether a prescriptive claim has been proved parcel by parcel.",
    },
    recordingGuidance:
      "New Jersey records deeds and easements with the county clerk, or register of deeds and mortgages in counties that use that office, where the property is located. Confirm the specific county office's current recording requirements before filing an easement agreement.",
    caseExample: {
      name: "Hazek v. Greene",
      citation: "51 N.J. Super. 545, 144 A.2d 199 (App. Div. 1958)",
      facts:
        "The operator of a livery stable and riding academy claimed prescriptive easements for bridle paths across two neighboring properties, with the trial court finding the claim proved as to one property but not the other.",
      outcome:
        "The Appellate Division affirmed both determinations, agreeing that the evidence supported a prescriptive easement over the first property while the proof for the second property, tied to a former trolley right-of-way, did not establish the required adverse use.",
      lesson:
        "Proof of a prescriptive easement in New Jersey must be assessed property by property, since the same claimant's use can be strong evidence on one parcel and insufficient on another.",
      sourceUrl: "https://law.justia.com/cases/new-jersey/appellate-division-published/1958/51-n-j-super-545-0.html",
    },
    sources: [
      {
        label: "New Jersey twenty-year right-of-entry statute applied to prescriptive easements",
        citation: "N.J. Stat. Ann. § 2A:14-6",
        url: "https://law.justia.com/codes/new-jersey/title-2a/section-2a-14-6/",
      },
      {
        label: "New Jersey's longer adverse-possession periods for the underlying fee",
        citation: "N.J. Stat. Ann. § 2A:14-30",
        url: "https://law.justia.com/codes/new-jersey/title-2a/section-2a-14-30/",
      },
    ],
  },
  {
    stateCode: "RI",
    distinctiveRule:
      "Rhode Island uses a comparatively short ten-year period, but a footway used only on foot cannot be acquired by prescription at all under a specific statutory carve-out.",
    overview:
      "Rhode Island's prescription statute sets a ten-year period for gaining rights through peaceful, uninterrupted possession or use under a claim of title, shorter than the twenty-year periods common elsewhere in the Northeast. A separate section of the same chapter denies the acquisition of a right of footway by prescription or adverse use for any length of time, unless the footway is claimed together with a right to pass by carriage or vehicle, an unusual and easily overlooked limitation.",
    burden:
      "The claimant must prove open, notorious, hostile use under a claim of right, continuous and uninterrupted for ten years. Mutual consent or an agreement between neighboring owners defeats the claim regardless of how long the shared use continued.",
    elements: [
      {
        name: "Open, notorious, and hostile use",
        explanation:
          "The use must be visible and inconsistent with the owner's exclusive rights, not a shared arrangement both sides understood as cooperative.",
      },
      {
        name: "Continuous, uninterrupted use for ten years",
        explanation:
          "The claimed use must persist without an effective break for the full ten-year statutory period.",
      },
      {
        name: "A claim of right, not mutual consent",
        explanation:
          "Use maintained by agreement or mutual accommodation between neighbors, rather than as an assertion of an independent right, does not satisfy the hostility element.",
      },
      {
        name: "A use outside the footway exclusion",
        explanation:
          "A pure footway right cannot be acquired by prescription in Rhode Island unless it is claimed together with a right to pass by carriage or vehicle over the same way.",
      },
    ],
    pitfalls: [
      "A shared driveway used by mutual consent and agreement between neighboring owners, even for decades, does not become a prescriptive easement because it was never adverse.",
      "Claiming only a footpath, without any vehicular component, risks running into the statutory bar on acquiring a pure right of footway by prescription.",
      "Evidence of hostility that arose only recently, after decades of cooperative use, will not satisfy the ten-year continuous requirement measured from that late point.",
      "A prescriptive easement claim over a driveway is distinct from a boundary-line dispute, which Rhode Island treats under separate principles even when the same strip of land is involved.",
    ],
    workedExample: {
      title: "A Smithfield driveway shared for forty years",
      facts:
        "Two neighboring families have shared a common driveway between their properties for forty years by informal mutual agreement, and only in the last two years has one owner tried to fence off part of it, prompting the other to claim a prescriptive easement.",
      analysis:
        "Because the decades of shared use were by mutual consent rather than a hostile claim, the long history does not establish adversity, and only the recent friction supplies any hostility, far short of the required ten continuous years of adverse use.",
      nextStep:
        "Look for any specific point when one party began asserting an exclusive right rather than a shared understanding, and calculate whether ten years of adverse use, measured from that point, has actually run.",
    },
    recordingGuidance:
      "Rhode Island records deeds and easements with the recorder of deeds, often housed within the town or city clerk's office, for the municipality where the property lies. Confirm that municipal office's current recording requirements before filing an easement agreement.",
    caseExample: {
      name: "Altieri v. Dolan",
      citation: "423 A.2d 482 (R.I. 1980)",
      facts:
        "Neighboring owners in Smithfield had shared a common driveway between their properties for roughly forty years, and the plaintiffs sued to enjoin a fence the defendants built along the boundary running through part of the driveway.",
      outcome:
        "The Rhode Island Supreme Court affirmed judgment for the defendants, agreeing that the decades of shared use had been by mutual consent and agreement between the owners rather than adverse, so no prescriptive easement had been established.",
      lesson:
        "Long-term shared use of a driveway by mutual understanding between neighbors, without a hostile assertion of right, does not mature into a prescriptive easement no matter how many years pass.",
      sourceUrl: "https://law.justia.com/cases/rhode-island/supreme-court/1980/423-a-2d-482.html",
    },
    sources: [
      {
        label: "Rhode Island ten-year prescription statute",
        citation: "R.I. Gen. Laws § 34-7-1",
        url: "https://webserver.rilegislature.gov/Statutes/TITLE34/34-7/34-7-1.htm",
      },
      {
        label: "Rhode Island statutory bar on acquiring a footway by prescription",
        citation: "R.I. Gen. Laws § 34-7-4",
        url: "https://webserver.rilegislature.gov/Statutes/TITLE34/34-7/34-7-4.htm",
      },
    ],
  },
  {
    stateCode: "VT",
    distinctiveRule:
      "Vermont ties its fifteen-year prescriptive period to the same statute that governs recovering possession of land generally, without a separate easement-specific statute.",
    overview:
      "Vermont applies a fifteen-year period for actions to recover possession of land, and its courts have extended that same period to prescriptive easement claims. A claimant must show open, notorious, hostile, and continuous use of a defined right for the full fifteen years, and Vermont decisions have cautioned that gaps in use, such as seasonal absence, do not necessarily break continuity if the character of the use matches what would ordinarily be expected for that kind of easement.",
    burden:
      "The claimant must prove hostile, open, and continuous use for fifteen years. An origin in the owner's express permission, such as permission to install a utility line, keeps the use permissive unless the claimant later asserted an adverse right and that adverse use itself continued for the full period.",
    elements: [
      {
        name: "Open and notorious use",
        explanation:
          "The use must be visible enough to give a reasonably attentive owner notice, whether through a maintained road, an installed pipe or line, or another apparent physical use.",
      },
      {
        name: "Hostile use under a claim of right",
        explanation:
          "The claimant must show conduct inconsistent with the owner's right to exclude, not a use that remains dependent on the owner's continuing consent.",
      },
      {
        name: "Continuous use for fifteen years",
        explanation:
          "The use must persist throughout the statutory period in the manner ordinary for the type of easement, allowing for gaps that fit the normal pattern of that use, such as seasonal camps.",
      },
      {
        name: "A definite, identifiable right",
        explanation:
          "The evidence must tie the claimed easement to a specific route, pipe, or structure rather than a vague, shifting pattern of use across the servient land.",
      },
    ],
    pitfalls: [
      "A use that began with the owner's express permission, such as permission to lay a water pipe, remains permissive unless the claimant can show a later, unmistakable shift to a hostile claim that itself lasted the full fifteen years.",
      "A sale of the burdened property to a new owner who is told about the use at closing does not automatically convert permissive use into an adverse claim against the new owner.",
      "Seasonal or camp-related gaps in use are not automatically fatal, but a claimant should be ready to show the pattern matched normal use for that kind of right.",
      "A prescriptive easement for one purpose, such as a water line, should not be assumed to support a broader right, such as vehicle access, that the historical use did not establish.",
    ],
    workedExample: {
      title: "A cabin's water line laid with a neighbor's permission",
      facts:
        "In 1965, cabin owners obtained permission from a neighboring landowner to install a water line to a spring after their own well failed. Successive owners of the neighboring lot, including one who bought the property and was told about the line at closing, never objected for many years afterward.",
      analysis:
        "Because the water line began with express permission, the years of quiet acceptance by successive owners do not automatically become adverse use; a claimant must show conduct that clearly asserted a right independent of that original permission, continuing for a full fifteen years afterward.",
      nextStep:
        "Identify precisely when, if ever, the cabin owners or their successors did something inconsistent with continued permission, such as refusing to remove the line on request, and count fifteen years forward from that point.",
    },
    recordingGuidance:
      "Vermont records deeds and easements in the town clerk's office for the town where the property is located, not with a county office. Confirm that specific town clerk's current recording requirements and include a clear description of the easement's route or facilities before filing.",
    caseExample: {
      name: "Guilbault v. Bowley",
      citation: "498 A.2d 1033 (Vt. 1985)",
      facts:
        "Neighboring landowners had given permission in 1965 for a water pipe to be installed across their property, and decades later a new owner of that property, who had been told about the pipe at closing, dug it up and the pipe's owners sued to establish a prescriptive easement.",
      outcome:
        "The Vermont Supreme Court held that the original permission defeated the prescriptive claim because nothing in the record showed the use had become hostile and continued adversely for a full fifteen years after any change from permissive to adverse use.",
      lesson:
        "A use that starts with the servient owner's permission does not ripen into a prescriptive easement in Vermont just because it continues quietly for many years afterward.",
      sourceUrl: "https://law.justia.com/cases/vermont/supreme-court/1985/84-044-0.html",
    },
    sources: [
      {
        label: "Vermont fifteen-year real-property limitations statute",
        citation: "12 V.S.A. § 501",
        url: "https://legislature.vermont.gov/statutes/section/12/023/00501",
      },
      {
        label: "Vermont town clerk recording duties",
        citation: "24 V.S.A. § 1154",
        url: "https://legislature.vermont.gov/statutes/section/24/033/01154",
      },
    ],
  },
];
